import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  linkedSignal,
  OnInit,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { durationStringToMinutes, ServiceEventType, ServiceGroup, TeamEventMeetingType } from '@vendasta/meetings';
import { BookMeNowStoreService } from '../book-me-now-store.service';
import { durationMinutes } from '../shared/duration.utils';
import { isEventUnavailable, MAX_BATCH_EVENTS } from '../util';
import { CartItem } from '../shared/booking-cart/booking-cart.component';
import { scrollLeftToReveal } from './chip-scroll';

type FlowType = 'service' | 'group' | 'calendar';

export interface Section {
  id: string;
  name: string;
  description: string;
  single: boolean;
  isGroup: boolean;
  events: ServiceEventType[];
}

export const ADDITIONAL_SECTION_ID = '__additional';
const SEARCH_COLLAPSE_MS = 200;
/** Where the cart and a stuck section header both pin, just under the sticky rail. Mirrors $sticky-offset. */
const STICKY_OFFSET_PX = 60;
/** Ignore the scroll-spy this long after a chip click so smooth scrolling cannot overwrite the chosen chip. */
const SPY_SUPPRESS_MS = 700;
/** A long smooth scroll outlasts SPY_SUPPRESS_MS, so the spy also waits until scrolling has been still this long. */
const SPY_SETTLE_MS = 150;
/** Room kept clear at each end of the chip row when revealing the active chip. */
const CHIP_EDGE_INSET_PX = 16;

@Component({
  selector: 'app-scheduling-page',
  standalone: false,
  templateUrl: './scheduling-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./scheduling-page.component.scss'],
  host: { '(window:scroll)': 'onWindowScroll()', '(document:keydown.escape)': 'sectionMenuOpen.set(false)' },
})
export class SchedulingPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly storeService = inject(BookMeNowStoreService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);

  searchQuery = signal('');
  searchOpen = signal(false);
  searchClosing = signal(false);
  loading = signal(true);
  loadError = signal(false);

  /** Which booking flow is active based on the route */
  flowType = signal<FlowType>('service');

  /** Group heading — shown only for group routes */
  groupName = signal('');
  groupDescription = signal('');

  groups = signal<ServiceGroup[]>([]);
  eventTypes = signal<ServiceEventType[]>([]);

  /** Whether multi-service cart mode is enabled (from Service.isBatchBookingEnabled) */
  isBatchBookingEnabled = signal(false);

  selectedEvents = signal<ServiceEventType[]>([]);

  private readonly additionalLabel = toSignal(this.translate.stream('SCHEDULING.ADDITIONAL_SERVICES'), {
    initialValue: 'Additional services',
  });
  private readonly servicesLabel = toSignal(this.translate.stream('BOOKING_STEPS.SERVICES'), {
    initialValue: 'Services',
  });

  sections = computed<Section[]>(() => {
    if (this.flowType() !== 'service') {
      const events = this.eventTypes();
      if (!events.length) return [];
      return [
        {
          id: 'group',
          name: this.groupName() || this.servicesLabel(),
          description: this.groupDescription(),
          single: !this.isBatchBookingEnabled(),
          isGroup: true,
          events,
        },
      ];
    }
    const groups = this.groups()
      .filter((g) => g.eventTypes?.length)
      .map((g) => ({
        id: g.id,
        name: g.name,
        description: g.description ?? '',
        single: !(this.isBatchBookingEnabled() && g.isBatchBookingEnabled),
        isGroup: true,
        events: g.eventTypes,
      }));
    const extra = this.eventTypes();
    if (!extra.length) return groups;
    return [
      ...groups,
      {
        id: ADDITIONAL_SECTION_ID,
        name: this.additionalLabel(),
        description: '',
        single: !this.isBatchBookingEnabled(),
        isGroup: false,
        events: extra,
      },
    ];
  });

  visibleSections = computed<Section[]>(() => {
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) return this.sections();
    return this.sections()
      .map((section) => ({
        ...section,
        events: section.events.filter(
          (e) => e.name?.toLowerCase().includes(query) || e.description?.toLowerCase().includes(query),
        ),
      }))
      .filter((section) => section.events.length);
  });

  /** "Select one" only earns its place when the booking allows several services but this group allows one. */
  showsSelectOneHint = computed(() => this.flowType() === 'service' && this.isBatchBookingEnabled());

  activeSectionId = linkedSignal(() => this.sections()[0]?.id ?? '');
  activeSectionName = computed(() => this.sections().find((s) => s.id === this.activeSectionId())?.name ?? '');
  sectionMenuOpen = signal(false);

  isMaxEventsReached = computed(() => this.isBatchBookingEnabled() && this.selectedEvents().length >= MAX_BATCH_EVENTS);

  cartItems = computed<CartItem[]>(() =>
    this.selectedEvents().map((event) => ({
      name: event.name,
      initials: this.getInitials(event.name),
      duration: event.duration ? durationStringToMinutes({ duration: event.duration }) : 0,
    })),
  );

  cartCtaLabel = computed(() => {
    const selected = this.selectedEvents();
    if (selected.length) {
      return this.needsProviderSelection(selected) ? 'BOOKING_CART.CHOOSE_PROVIDER' : 'BOOKING_CART.CHOOSE_DATE_TIME';
    }
    const allEvents = [...this.eventTypes(), ...this.groups().flatMap((g) => g.eventTypes || [])];
    if (this.needsProviderSelection(allEvents)) {
      return 'BOOKING_CART.CHOOSE_PROVIDER';
    }
    const hasClientChoice = allEvents.some((e) => {
      const type = e.eventType ?? TeamEventMeetingType.ROUND_ROBIN;
      return type === TeamEventMeetingType.CLIENT_CHOICE;
    });
    return hasClientChoice ? 'BOOKING_CART.CHOOSE_PROVIDER' : 'BOOKING_CART.CHOOSE_DATE_TIME';
  });

  durationMinutes = durationMinutes;

  /** Tracks which group each selected event came from (eventId → groupId) */
  private eventSourceGroupMap = new Map<string, string>();
  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  private readonly chipRow = viewChild<ElementRef<HTMLElement>>('chipRow');
  private lastScrollY = 0;
  private suppressSpyUntil = 0;

  constructor() {
    effect(() => {
      if (this.searchOpen()) this.searchInput()?.nativeElement.focus();
    });
    afterRenderEffect(() => {
      this.visibleSections();
      untracked(() => this.spyActiveSection());
    });
    afterRenderEffect(() => {
      const row = this.chipRow()?.nativeElement;
      const activeId = this.activeSectionId();
      untracked(() => {
        if (row?.clientWidth) this.revealChip(row, activeId);
      });
    });
  }

  ngOnInit(): void {
    const serviceId = this.route.snapshot.params.serviceId;
    const serviceSlug = this.route.snapshot.params.serviceSlug;
    const groupId = this.route.snapshot.params['groupId'];
    const groupSlug = this.route.snapshot.params['groupSlug'];
    const calendarId = this.route.snapshot.params['calendarId'];

    // Restore selections from store before any load (loadServiceV2 clears them)
    const savedEvents = this.storeService.getSelectedBookingEvents();
    const savedGroupMap = this.storeService.getEventSourceGroupMap();

    if (serviceId && serviceSlug) {
      this.flowType.set('service');
      this.storeService.setBookingOriginId(serviceId);
      this.storeService
        .loadServiceV2({ serviceId })
        .pipe(
          catchError((err) => {
            this.storeService.handleMeetingTypeError(err);
            return of(null);
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((response) => {
          if (response) {
            if (response.bookingUrl && !response.bookingUrl.includes(serviceSlug)) {
              this.storeService.setErrorDisplay('slug-mismatch-error');
              this.loading.set(false);
              return;
            }
            this.isBatchBookingEnabled.set(response.isBatchBookingEnabled ?? false);
            const groups = (response.groups || []).map((g) => ({
              ...g,
              eventTypes: (g.eventTypes || []).map((e) => this.toServiceEventType(e)),
            }));
            this.groups.set(groups as any);
            this.eventTypes.set((response.eventTypes || []).map((e) => this.toServiceEventType(e)));
          }
          this.loading.set(false);
          if (savedEvents.length) {
            this.selectedEvents.set(savedEvents);
            this.eventSourceGroupMap = savedGroupMap;
          }
          this.updateProviderStepFlag();
          this.preselectEventFromQueryParams();
        });
    } else if (groupId && groupSlug) {
      this.flowType.set('group');
      this.searchOpen.set(true);
      this.storeService.setBookingOriginId(groupId);
      this.loadGroup(groupId, groupSlug, savedEvents);
    } else if (calendarId) {
      this.flowType.set('calendar');
      this.searchOpen.set(true);
      this.storeService.setBookingOriginId(calendarId);
      this.loadCalendarMeetingTypes(calendarId, savedEvents);
    } else {
      this.loading.set(false);
      this.loadError.set(true);
    }
  }

  isEventUnavailable(event: ServiceEventType): boolean {
    return isEventUnavailable(event);
  }

  isEventSelected(event: ServiceEventType): boolean {
    return this.selectedEvents().some((e) => e.id === event.id);
  }

  /**
   * A radio group with a selected sibling can always switch, since the switch replaces rather than adds.
   * With batch booking off the whole menu is one radio group, so nothing is ever blocked.
   */
  isRowDisabled(event: ServiceEventType, section: Section): boolean {
    if (this.isEventSelected(event) || !this.isBatchBookingEnabled()) return false;
    const canSwapSibling = section.single && this.selectedSiblings(section).length > 0;
    if (this.isMaxEventsReached()) return !canSwapSibling;
    return false;
  }

  onRowToggle(event: ServiceEventType, section: Section): void {
    if (this.isEventUnavailable(event) || this.isRowDisabled(event, section)) return;
    if (this.isEventSelected(event)) {
      this.removeEventFromCart(event.id);
    } else {
      const replaced = this.isBatchBookingEnabled()
        ? section.single
          ? this.selectedSiblings(section)
          : []
        : this.selectedEvents();
      replaced.forEach((e) => this.removeEventFromCart(e.id));
      this.addEventToCart(event, section.isGroup ? section.id : undefined);
    }
    this.syncNonServiceFlow();
  }

  onRemoveCartItem(index: number): void {
    const event = this.selectedEvents()[index];
    if (!event) return;
    this.removeEventFromCart(event.id);
    this.syncNonServiceFlow();
  }

  goToSection(id: string): void {
    this.activeSectionId.set(id);
    this.sectionMenuOpen.set(false);
    this.searchQuery.set('');
    this.suppressSpyUntil = Date.now() + SPY_SUPPRESS_MS;
    document.getElementById('section-' + id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  toggleSearch(): void {
    if (this.searchOpen()) this.closeSearch();
    else this.openSearch();
  }

  openSearch(): void {
    this.searchClosing.set(false);
    this.searchOpen.set(true);
    this.lastScrollY = window.scrollY;
  }

  closeSearch(): void {
    if (!this.searchOpen() || this.searchClosing()) return;
    this.searchClosing.set(true);
    setTimeout(() => {
      this.searchOpen.set(false);
      this.searchClosing.set(false);
      this.searchQuery.set('');
    }, SEARCH_COLLAPSE_MS);
  }

  /** Scrolling down reclaims the space, but only while the field is empty so an active search is never wiped. */
  onWindowScroll(): void {
    const y = window.scrollY;
    const scrollingDown = y > this.lastScrollY + 4;
    this.lastScrollY = y;
    if (scrollingDown && this.searchOpen() && !this.searchQuery() && this.flowType() === 'service') {
      this.closeSearch();
    }
    this.spyActiveSection();
  }

  /**
   * The highlighted chip follows the topmost section still visible below the rail, unless a chip was just clicked.
   * At the bottom of the page the last sections can't reach the top, so a chosen one stays while it is on screen.
   */
  private spyActiveSection(): void {
    if (Date.now() < this.suppressSpyUntil) {
      this.suppressSpyUntil = Math.max(this.suppressSpyUntil, Date.now() + SPY_SETTLE_MS);
      return;
    }
    const sections = this.visibleSections();
    if (!sections.length) return;
    const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
    const active = document.getElementById('section-' + this.activeSectionId())?.getBoundingClientRect();
    if (atBottom && active && active.bottom > STICKY_OFFSET_PX && active.top < window.innerHeight) return;
    const topVisible =
      sections.find((section) => {
        const bottom = document.getElementById('section-' + section.id)?.getBoundingClientRect().bottom ?? -Infinity;
        return bottom > STICKY_OFFSET_PX;
      }) ?? sections[sections.length - 1];
    if (topVisible.id !== this.activeSectionId()) this.activeSectionId.set(topVisible.id);
  }

  /** The chip row scrolls on its own, so the active chip is brought into view there without moving the page. */
  private revealChip(row: HTMLElement, sectionId: string): void {
    const chip = Array.from(row.querySelectorAll<HTMLElement>('[data-section-id]')).find(
      (el) => el.dataset['sectionId'] === sectionId,
    );
    if (!chip) return;
    const rowRect = row.getBoundingClientRect();
    const chipRect = chip.getBoundingClientRect();
    const left = scrollLeftToReveal(
      { scrollLeft: row.scrollLeft, width: row.clientWidth },
      { left: chipRect.left - rowRect.left + row.scrollLeft, width: chipRect.width },
      CHIP_EDGE_INSET_PX,
    );
    if (Math.abs(left - row.scrollLeft) > 1) row.scrollTo({ left, behavior: 'smooth' });
  }

  getInitials(name: string): string {
    if (!name) {
      return '';
    }
    return name
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase())
      .join('')
      .slice(0, 2);
  }

  onChooseProvider(): void {
    if (this.flowType() === 'service') {
      this.storeService.setSelectedBookingEvents(this.selectedEvents());
      this.storeService.setEventSourceGroupMap(this.eventSourceGroupMap);
    } else {
      this.syncSelectedEventsToStore();
    }

    const needsProvider = this.needsProviderSelection(this.selectedEvents());
    this.storeService.setProviderStepSkipped(!needsProvider);
    this.storeService.setBookingStep(needsProvider ? 'provider' : 'datetime');
  }

  /** Reads the unfiltered section so a search cannot hide the sibling that is currently selected. */
  private selectedSiblings(section: Section): ServiceEventType[] {
    const full = this.sections().find((s) => s.id === section.id) ?? section;
    return full.events.filter((e) => this.isEventSelected(e));
  }

  private syncNonServiceFlow(): void {
    if (this.flowType() !== 'service') {
      this.syncSelectedEventsToStore();
    }
  }

  private needsProviderSelection(events: ServiceEventType[]): boolean {
    return events.some((event) => {
      const type = event.eventType ?? TeamEventMeetingType.ROUND_ROBIN;
      if (type !== TeamEventMeetingType.CLIENT_CHOICE) {
        return false;
      }
      const allHosts = event.hostUser || [];
      const hasConnected = allHosts.some((h: any) => h.isConnected === true);
      const connectedProviders = hasConnected ? allHosts.filter((h: any) => h.isConnected === true) : allHosts;
      return connectedProviders.length > 1;
    });
  }

  /**
   * Preselects the event types named by the entry URL's entity_id query param
   * (e.g. shared links like Reserve with Google deep-link specific events).
   * Accepts repeated params and comma-separated ids. Never overrides an
   * existing selection restored from the store; single-select pages take the
   * first valid id, batch pages add each id through the normal cart rules.
   */
  private preselectEventFromQueryParams(): void {
    if (this.selectedEvents().length) {
      return;
    }
    const raw = this.route.snapshot.queryParams['entity_id'];
    const values: string[] = Array.isArray(raw) ? raw : raw ? [raw] : [];
    const entityIds = [
      ...new Set(
        values
          .flatMap((value) => value.split(','))
          .map((id) => id.trim())
          .filter((id) => id.startsWith('MT-')),
      ),
    ];

    for (const entityId of entityIds) {
      this.preselectEventById(entityId);
      if (!this.isBatchBookingEnabled() && this.selectedEvents().length) {
        return;
      }
    }
  }

  private preselectEventById(entityId: string): void {
    const standalone = this.eventTypes().find((e) => e.id === entityId);
    if (standalone) {
      this.preselectEvent(standalone);
      return;
    }
    for (const group of this.groups()) {
      const event = (group.eventTypes || []).find((e) => e.id === entityId);
      if (event) {
        this.preselectEvent(event, group.id);
        return;
      }
    }
  }

  private preselectEvent(event: ServiceEventType, groupId?: string): void {
    if (this.isEventUnavailable(event)) {
      return;
    }
    this.addEventToCart(event, groupId);
    this.syncNonServiceFlow();
  }

  private addEventToCart(event: ServiceEventType, groupId?: string): void {
    if (!this.isBatchBookingEnabled()) {
      this.eventSourceGroupMap.clear();
      if (groupId) {
        this.eventSourceGroupMap.set(event.id, groupId);
      }
      this.selectedEvents.set([event]);
      this.updateProviderStepFlag();
      return;
    }
    const current = this.selectedEvents();
    const alreadyExists = current.some((e) => e.id === event.id);
    if (alreadyExists || current.length >= MAX_BATCH_EVENTS) {
      return;
    }
    if (groupId) {
      this.eventSourceGroupMap.set(event.id, groupId);
    }
    this.selectedEvents.update((list) => [...list, event]);
    this.updateProviderStepFlag();
  }

  private removeEventFromCart(eventId: string): void {
    this.eventSourceGroupMap.delete(eventId);
    this.selectedEvents.update((current) => current.filter((e) => e.id !== eventId));
    this.updateProviderStepFlag();
  }

  private updateProviderStepFlag(): void {
    const allEvents = [...this.eventTypes(), ...this.groups().flatMap((g) => g.eventTypes || [])];
    if (this.needsProviderSelection(allEvents)) {
      this.storeService.setShouldShowProviderStep(true);
    } else {
      const hasClientChoice = allEvents.some((e) => {
        const type = e.eventType ?? TeamEventMeetingType.ROUND_ROBIN;
        return type === TeamEventMeetingType.CLIENT_CHOICE;
      });
      this.storeService.setShouldShowProviderStep(hasClientChoice);
    }

    const selected = this.selectedEvents();
    if (selected.length) {
      this.storeService.setProviderStepSkipped(!this.needsProviderSelection(selected));
    } else {
      this.storeService.setProviderStepSkipped(false);
    }
  }

  private loadGroup(groupId: string, groupSlug: string, savedEvents: ServiceEventType[]): void {
    this.storeService
      .loadGroupV2({ groupId })
      .pipe(
        catchError((err) => {
          this.storeService.handleMeetingTypeError(err);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((group) => {
        if (group) {
          if (group.bookingUrl && !group.bookingUrl.includes(groupSlug)) {
            this.storeService.setErrorDisplay('slug-mismatch-error');
            this.loading.set(false);
            return;
          }
          this.groupName.set(group.name || '');
          this.groupDescription.set(group.description || '');
          this.isBatchBookingEnabled.set(group.isBatchBookingEnabled ?? false);
          this.eventTypes.set((group.eventTypes || []).map((e) => this.toServiceEventType(e)));
        }
        this.loading.set(false);
        this.restoreValidSelections(savedEvents);
        this.preselectEventFromQueryParams();
      });
  }

  private loadCalendarMeetingTypes(calendarId: string, savedEvents: ServiceEventType[]): void {
    this.storeService
      .loadMeetingTypes({ calendarId, calendarSlug: calendarId })
      .pipe(
        catchError((err) => {
          this.storeService.handleMeetingTypeError(err);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((meetingTypes) => {
        if (meetingTypes) {
          this.eventTypes.set(
            meetingTypes.map(
              (mt) =>
                ({
                  id: mt.id,
                  name: mt.name,
                  description: mt.description,
                  duration: mt.duration,
                  calendarId,
                  eventType: mt.meetingType ?? TeamEventMeetingType.ROUND_ROBIN,
                  hostUser: mt.hostUsers,
                  locationType: mt.locationType,
                  location: mt.location,
                  locationGuideline: mt.locationGuidelines,
                  notificationType: mt.notificationType,
                  smsRequired: mt.isPhoneNumberRequired,
                  serviceForm: mt.form || undefined,
                }) as unknown as ServiceEventType,
            ),
          );
          // Calendar flow is always single-select
          this.isBatchBookingEnabled.set(false);
        }
        this.loading.set(false);
        this.restoreValidSelections(savedEvents);
      });
  }

  /** Restore saved selections only if they exist in the currently loaded event types.
   * Re-maps saved events against the loaded (and potentially enriched) set by id so
   * restored selections carry all enriched fields (smsRequired, notificationType, etc.).
   */
  private restoreValidSelections(savedEvents: ServiceEventType[]): void {
    if (savedEvents.length) {
      const loadedById = new Map(this.eventTypes().map((e) => [e.id, e]));
      const valid = savedEvents
        .filter((e) => loadedById.has(e.id))
        .map((e) => loadedById.get(e.id) as ServiceEventType);
      if (valid.length) {
        this.selectedEvents.set(valid);
      }
    }
    this.updateProviderStepFlag();
  }

  /**
   * Normalize a raw association/meeting-type object to ServiceEventType.
   * Uses spread to preserve all fields (emailRequired, smsRequired,
   * notificationType, locationType, serviceForm, etc.) for downstream pages.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private toServiceEventType(e: any): ServiceEventType {
    let hosts = e.hostUsers || e.hostUser || [];
    if (hosts.length === 0 && e.creatorUserName) {
      hosts = [
        {
          userId: e.identifier || e.id,
          displayName: e.creatorUserName,
          isConnected: true,
        },
      ];
    }
    const meetingType = e.teamMeetingType ?? e.teamEventType ?? e.eventType ?? TeamEventMeetingType.ROUND_ROBIN;
    return {
      ...e,
      eventType: meetingType,
      hostUser: hosts,
      locationGuideline: e.locationGuideline ?? e.locationGuidelines,
    } as unknown as ServiceEventType;
  }

  private syncSelectedEventsToStore(): void {
    this.storeService.setSelectedBookingEvents(this.selectedEvents().map((e) => this.toServiceEventType(e)));
  }
}
